import React, { useState, useEffect } from "react";
import {
  FaGraduationCap,
  FaUserGraduate,
  FaSchool,
  FaBookOpen,
  FaBirthdayCake,
  FaLaptopCode,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

function Education() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    // Check for mobile viewport
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Set up listener for window resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const educationDetails = [
    {
      level: "Post-Graduation",
      institution: "Indian Institute of Information Technology Allahabad",
      degree:
        "Master in Information Technology (specialization in Software Engineering)",
      year: "2024 - Present",
      details:
        "Diving deep into the realms of **Software Development**, **Software Testing**, and **Deep Learning**. Exploring cutting-edge technologies implementing **AI/ML in Software**.",
      courses: [
        "Advanced Algorithms",
        "Machine Learning",
        "Software Architecture",
        "Cloud Computing",
      ],
      certificates: [
        // "AWS Cloud Practitioner",
        // "Google Cloud Platform Fundamentals",
        // "Azure Fundamentals",
      ],
      image: "/images/education/post-grad-2.png",
    },
    {
      type: "experience",
      company: "Tata Consultancy Services",
      role: "System Engineer",
      year: "2019 - 2024",
      details:
        "Worked on Enterprise-level Software Development and Support Projects",
      image: "/images/education/work_tcs.png",
    },
    {
      level: "Graduation",
      institution: "Maulana Abul Kalam Azad University of Technology",
      degree: "Bachelor of Technology in Computer Science & Engineering",
      year: "2015 - 2019",
      details:
        "Graduated with an impressive **GPA of 8.43/10**. Developed a *College Guidance Chatbot* as the capstone project.",
      courses: [
        "Data Structures",
        "Operating Systems",
        "Database Management",
        "Web Development",
      ],
      certificates: [
        "NPTEL Java Certification",
        "Web Development Bootcamp",
        "Python Programming",
      ],
      image: "/images/education/grad.jpg",
    },
    {
      level: "Senior-Secondary",
      institution: "Kendriya Vidyalaya Barrackpore (ARMY)",
      degree: "Senior Secondary Certificate from CBSE",
      year: "2013 - 2015",
      details:
        "Achieved a stellar **83.4% in Higher Secondary**, focusing on *PCM* and *CS*. Actively participated in numerous *coding competitions* and *olympiads*. Honed problem-solving skills and algorithmic thinking.",
      courses: [
        "Data Structures",
        "Operating Systems",
        "Database Management",
        "Web Development",
      ],
      certificates: ["Central Board of Senior Secondary Certificate"],
      image: "/images/education/senior-secondary.jpg",
    },
    {
      level: "Secondary",
      institution: "Kendriya Vidyalaya Barrackpore (ARMY)",
      degree: "Secondary School Certificate from CBSE",
      year: "2009 - 2013",
      details:
        "Excelled with a remarkable **GPA of 9.6/10**, demonstrating exceptional academic prowess. Developed a strong foundation in *sciences* and *mathematics*. Ignited a passion for logical reasoning and analytical thinking.",
      courses: ["Mathematics", "English", "Science"],
      certificates: ["Central Board of Secondary Certificate"],
      image: "/images/education/secondary.jpg",
    },
  ];

  const getIcon = (level) => {
    switch (level) {
      case "Post-Graduation":
        return (
          <FaUserGraduate className="text-emerald-400 text-xl md:text-2xl" />
        );
      case "Graduation":
        return (
          <FaGraduationCap className="text-emerald-400 text-xl md:text-2xl" />
        );
      case "experience":
        return (
          <FaLaptopCode className="text-emerald-400 text-xl md:text-2xl" />
        );
      case "Senior-Secondary":
        return <FaSchool className="text-emerald-400 text-xl md:text-2xl" />;
      default:
        return <FaBookOpen className="text-emerald-400 text-xl md:text-2xl" />;
    }
  };

  const renderFormattedText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="text-emerald-400">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("*") && part.endsWith("*")) {
        return (
          <em key={index} className="text-emerald-300">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  // Render education details in a mobile-friendly manner
  const renderMobileEducationItem = (edu, index) => {
    if (edu.type === "experience") {
      return (
        <motion.div
          key={index}
          className="mb-8 relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
        >
          <div className="flex items-center mb-3">
            <div className="bg-purple-600 rounded-full p-3 z-10 border-2 border-emerald-400 mr-4">
              {getIcon(edu.type || edu.level)}
            </div>
            <div>
              <Link
                to="/experience"
                className="text-purple-400 text-xl font-bold flex items-center gap-2"
              >
                {edu.company} <FaExternalLinkAlt className="text-sm" />
              </Link>
              <p className="text-lg font-bold text-gray-300">{edu.role}</p>
              <p className="text-md font-bold text-emerald-400">{edu.year}</p>
            </div>
          </div>
          <div className="pl-16">
            <p className="text-gray-400">{edu.details}</p>
          </div>
        </motion.div>
      );
    } else {
      return (
        <motion.div
          key={index}
          className="mb-8 relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
        >
          <div className="flex items-center mb-3">
            <div className="bg-gray-800 rounded-full p-3 z-10 border-2 border-emerald-400 mr-4">
              {getIcon(edu.type || edu.level)}
            </div>
            <div>
              <h3 className="text-emerald-400 text-xl font-bold">
                {edu.level}
              </h3>
              <p className="text-lg font-bold text-gray-300">
                {edu.institution}
              </p>
            </div>
          </div>
          <div className="pl-16">
            <p className="text-base italic font-light text-gray-400 mb-1">
              {edu.degree}
            </p>
            <p className="text-md font-bold text-emerald-400 mb-2">
              {edu.year}
            </p>
            <div
              onClick={() =>
                setHoveredIndex(hoveredIndex === index ? null : index)
              }
              className="cursor-pointer text-emerald-300 underline text-sm"
            >
              {hoveredIndex === index ? "Hide details" : "Show details"}
            </div>

            {hoveredIndex === index && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-2 bg-gray-800 rounded-lg shadow-xl p-4 border border-emerald-400"
              >
                <p className="text-gray-300 font-serif leading-relaxed mb-4">
                  {renderFormattedText(edu.details)}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-emerald-400 font-bold mb-2">
                      Key Courses
                    </h4>
                    <ul className="text-sm text-gray-400">
                      {edu.courses.map((course, idx) => (
                        <li key={idx} className="mb-1">
                          • {course}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-emerald-400 font-bold mb-2">
                      Certificates
                    </h4>
                    <ul className="text-sm text-gray-400">
                      {edu.certificates.map((cert, idx) => (
                        <li key={idx} className="mb-1">
                          • {cert}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      );
    }
  };

  // Render education details in desktop layout
  const renderDesktopEducationItem = (edu, index) => {
    if (edu.type === "experience") {
      return (
        <div key={index} className="mb-24 relative">
          <div
            className="flex items-center justify-center absolute left-1/2 transform -translate-x-1/2"
            style={{ top: "-12px" }}
          >
            <motion.div
              className="bg-purple-600 rounded-full p-3 z-10 cursor-pointer border-2 border-emerald-400"
              whileHover={{ scale: 1.1 }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {getIcon(edu.type || edu.level)}
            </motion.div>
          </div>

          <Link to="/experience" className="block w-1/2 ml-auto pl-12">
            <div className="bg-purple-900/50 p-6 rounded-lg border border-purple-500 hover:border-emerald-400 transition-all duration-300">
              <h3 className="text-2xl font-bold text-purple-400 mb-2 flex items-center gap-2">
                {edu.company} <FaExternalLinkAlt className="text-sm" />
              </h3>
              <p className="text-lg font-bold text-gray-300">{edu.role}</p>
              <p className="text-lg font-bold text-emerald-400">{edu.year}</p>
              <p className="text-gray-400 mt-2">{edu.details}</p>
            </div>
          </Link>
        </div>
      );
    } else {
      return (
        <div key={index} className="mb-24 relative">
          <div
            className="flex items-center justify-center absolute left-1/2 transform -translate-x-1/2"
            style={{ top: "-12px" }}
          >
            <motion.div
              className="bg-gray-800 rounded-full p-3 z-10 cursor-pointer border-2 border-emerald-400"
              whileHover={{ scale: 1.1 }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {getIcon(edu.type || edu.level)}
            </motion.div>
          </div>

          <div
            className={`w-1/2 ${
              index % 2 === 0 ? "pr-12 text-right" : "pl-12 text-left ml-auto"
            }`}
          >
            <div className="bg-gray-600/50 p-6 rounded-lg border border-gray-700 hover:border-emerald-400 transition-all duration-300">
              <h3 className="text-2xl font-bold text-emerald-400 mb-2">
                {edu.level}
              </h3>
              <p className="text-lg font-bold text-gray-300">
                {edu.institution}
              </p>
              <p className="text-base italic font-light text-gray-400 mb-1">
                {edu.degree}
              </p>
              <p className="text-lg font-bold text-emerald-400">{edu.year}</p>
            </div>
          </div>

          {hoveredIndex === index && !edu.type && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className={`bg-gray-800 rounded-lg shadow-xl p-6 absolute ${
                index % 2 === 0 ? "left-1/2 ml-8" : "right-1/2 mr-8"
              } top-0 border border-emerald-400`}
              style={{ width: "400px", zIndex: 20 }}
            >
              <img
                src={edu.image}
                alt={edu.institution}
                className="w-full h-40 object-cover mb-4 rounded"
              />
              <p className="text-gray-300 font-serif leading-relaxed mb-4">
                {renderFormattedText(edu.details)}
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-emerald-400 font-bold mb-2">
                    Key Courses
                  </h4>
                  <ul className="text-sm text-gray-400">
                    {edu.courses.map((course, idx) => (
                      <li key={idx} className="mb-1">
                        • {course}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-emerald-400 font-bold mb-2">
                    Certificates
                  </h4>
                  <ul className="text-sm text-gray-400">
                    {edu.certificates.map((cert, idx) => (
                      <li key={idx} className="mb-1">
                        • {cert}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-br from-gray-800 to-gray-900 opacity-95 py-28 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-5xl font-extrabold text-center font-display mb-12 md:mb-20"
        >
          <span className="font-['Inter'] tracking-tight text-emerald-400">
            Academic Journey
          </span>
          <p className="text-white text-lg font-bold mt-2">
            Learning, evolving, excelling.
          </p>
        </motion.h2>

        {isMobile ? (
          // Mobile view - Stacked layout
          <div className="relative">
            <div
              className="absolute left-8 top-0 w-0.5 bg-emerald-800"
              style={{ height: "calc(100% - 80px)" }}
            />

            {educationDetails.map((edu, index) =>
              renderMobileEducationItem(edu, index)
            )}

            <div className="flex items-center justify-center mt-12 ml-2 relative">
              <div className="bg-red-600 rounded-full p-3 z-10 border-2 border-emerald-400">
                <FaBirthdayCake className="text-white text-xl" />
              </div>
              <p className="text-lg font-bold text-red-400 ml-4">
                November 15, 1997
              </p>
            </div>
          </div>
        ) : (
          // Desktop view - Timeline layout
          <div className="relative">
            <div
              className="absolute left-1/2 top-0 w-0.5 bg-emerald-800 transform -translate-x-1/2"
              style={{ height: "calc(100% - 80px)" }}
            />

            {educationDetails.map((edu, index) =>
              renderDesktopEducationItem(edu, index)
            )}

            <div className="flex items-center justify-center mt-12 relative">
              <div className="bg-red-600 rounded-full p-4 z-10 border-2 border-emerald-400">
                <FaBirthdayCake className="text-white text-2xl" />
              </div>
            </div>
            <p className="text-lg font-bold text-red-400 text-center mt-4">
              November 15, 1997
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default Education;
